<template>
  <div v-if="visible" class="modal-overlay">
    <div class="modal-content">
      <div class="modal-header">
        <h3>{{ s__('TaskClone|Clone Task') }}</h3>
        <button class="modal-close" aria-label="Close" @click="close">
          <CloseIcon />
        </button>
      </div>

      <div class="modal-body">
        <div class="task-info">
          <span class="task-title">{{ task.title }}</span>
        </div>

        <div v-if="loadingOptions" class="loading-options">
          {{ __('Loading...') }}
        </div>

        <template v-else>
          <div class="form-section">
            <label class="section-label">{{
              s__('TaskClone|Default time for cloned tasks')
            }}</label>
            <div class="time-input-24h">
              <select v-model="defaultHour" class="time-select">
                <option v-for="h in hours" :key="h" :value="h">{{ h }}</option>
              </select>
              <span class="time-separator">:</span>
              <select v-model="defaultMinute" class="time-select">
                <option v-for="m in minutes" :key="m" :value="m">{{ m }}</option>
              </select>
            </div>
          </div>

          <div class="form-section">
            <label class="section-label">{{ s__('TaskClone|Select dates') }}</label>
            <p class="help-text">
              {{
                format(
                  s__('TaskClone|Click on dates to add or remove them. Maximum %{max} dates.'),
                  {
                    max: maxDates,
                  }
                )
              }}
            </p>

            <div class="calendar-container">
              <div class="calendar-header">
                <button class="nav-btn" @click="prevMonth">&lsaquo;</button>
                <span class="month-year">{{ monthYearDisplay }}</span>
                <button class="nav-btn" @click="nextMonth">&rsaquo;</button>
              </div>

              <div class="weekdays">
                <span v-for="day in weekdayLabels" :key="day" class="weekday">{{ day }}</span>
              </div>

              <div class="days-grid">
                <div
                  v-for="(day, index) in calendarDays"
                  :key="index"
                  :class="[
                    'day-cell',
                    {
                      'other-month': day.otherMonth,
                      today: day.isToday,
                      selected: isDateSelected(day.date),
                      past: day.isPast && !day.isToday,
                    },
                  ]"
                  @click="toggleDate(day)"
                >
                  <span class="day-number">{{ day.day }}</span>
                </div>
              </div>
            </div>
          </div>

          <div v-if="selectedDates.length > 0" class="selected-dates-section">
            <label class="section-label">{{
              format(s__('TaskClone|Selected dates (%{count})'), { count: selectedDates.length })
            }}</label>
            <div class="selected-dates-list">
              <div
                v-for="(dateInfo, index) in selectedDates"
                :key="dateInfo.date"
                class="selected-date-item"
              >
                <span class="date-display">{{ formatDateDisplay(dateInfo.date) }}</span>
                <div class="time-input-24h-small">
                  <select
                    v-model="dateInfo.hour"
                    class="time-select-small"
                    :title="s__('TaskClone|Hour')"
                  >
                    <option v-for="h in hours" :key="h" :value="h">{{ h }}</option>
                  </select>
                  <span class="time-separator-small">:</span>
                  <select
                    v-model="dateInfo.minute"
                    class="time-select-small"
                    :title="s__('TaskClone|Minute')"
                  >
                    <option v-for="m in minutes" :key="m" :value="m">{{ m }}</option>
                  </select>
                </div>
                <button
                  class="remove-btn"
                  :title="s__('TaskClone|Remove this date')"
                  @click="removeDate(index)"
                >
                  &times;
                </button>
              </div>
            </div>
          </div>

          <!-- Clone Options Section -->
          <div v-if="hasAnyCloneableAttributes" class="form-section">
            <label class="section-label">{{ s__('TaskClone|What to clone') }}</label>
            <div class="clone-options mobile-underlined-checklist">
              <!-- Personal Labels (only show if cloner has personal labels on this task) -->
              <label
                v-if="hasClonerPersonalLabels"
                class="checkbox-label mobile-underlined-checklist-item"
              >
                <input v-model="cloneOptions.clonePersonalLabels" type="checkbox" />
                <span>{{ s__('TaskClone|Personal Labels') }}</span>
              </label>

              <!-- Project Labels (only show if task has any) -->
              <label
                v-if="hasProjectLabels"
                class="checkbox-label mobile-underlined-checklist-item"
              >
                <input v-model="cloneOptions.cloneProjectLabels" type="checkbox" />
                <span>{{ s__('TaskClone|Project Labels') }}</span>
              </label>

              <!-- Parent Task (only show if task has one) -->
              <label v-if="hasParentTask" class="checkbox-label mobile-underlined-checklist-item">
                <input v-model="cloneOptions.cloneParentTask" type="checkbox" />
                <span>{{ s__('TaskClone|Parent Task') }}</span>
              </label>

              <!-- Checklist (only show if present) -->
              <label v-if="hasChecklist" class="checkbox-label mobile-underlined-checklist-item">
                <input v-model="cloneOptions.cloneChecklist" type="checkbox" />
                <span>{{ s__('TaskClone|Checklist') }}</span>
              </label>

              <!-- Assignee (only show if present) -->
              <label v-if="hasAssignee" class="checkbox-label mobile-underlined-checklist-item">
                <input v-model="cloneOptions.cloneAssignee" type="checkbox" />
                <span>{{ s__('TaskClone|Assignee') }}</span>
              </label>

              <!-- Subscribers (only show if any) -->
              <label v-if="hasSubscribers" class="checkbox-label mobile-underlined-checklist-item">
                <input v-model="cloneOptions.cloneSubscribers" type="checkbox" />
                <span>{{ s__('TaskClone|Subscribers') }}</span>
              </label>

              <!-- Description (only show if present) -->
              <label v-if="hasDescription" class="checkbox-label mobile-underlined-checklist-item">
                <input v-model="cloneOptions.cloneDescription" type="checkbox" />
                <span>{{ s__('TaskClone|Description') }}</span>
              </label>

              <!-- Confidentiality (only show if task is confidential) -->
              <label v-if="isConfidential" class="checkbox-label mobile-underlined-checklist-item">
                <input v-model="cloneOptions.cloneConfidential" type="checkbox" />
                <span>{{ s__('TaskClone|Confidentiality') }}</span>
              </label>

              <!-- Confidential Viewers (only show if task is confidential and has viewers) -->
              <label
                v-if="hasConfidentialViewers"
                class="checkbox-label mobile-underlined-checklist-item"
              >
                <input v-model="cloneOptions.cloneConfidentialViewers" type="checkbox" />
                <span>{{ s__('TaskClone|Confidential Viewers') }}</span>
              </label>
            </div>
          </div>

          <div v-if="error" class="error-message">{{ error }}</div>
        </template>
      </div>

      <div class="modal-footer mobile-stretch-btn-group">
        <button class="btn-secondary" :disabled="loading" @click="close">
          {{ __('Cancel') }}
        </button>
        <button
          class="btn-primary"
          :disabled="loading || loadingOptions || selectedDates.length === 0"
          @click="submitClone"
        >
          <span v-if="loading">{{ s__('TaskClone|Creating...') }}</span>
          <span v-else>{{
            format(s__('TaskClone|Create %{count} clones'), { count: selectedDates.length })
          }}</span>
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch } from 'vue'
import { useMutation, useApolloClient } from '@vue/apollo-composable'
import gql from 'graphql-tag'
import { __, s__ } from '~/locale'
import CloseIcon from '../../common/icons/CloseIcon.vue'
import { useBodyScrollLock } from '@/composables/useBodyScrollLock'
import '../../../styles/mobile-underlined-checklist.css'

const props = defineProps({
  visible: { type: Boolean, default: false },
  task: { type: Object, required: true },
  projectId: { type: [String, Number], required: true },
  locale: { type: String, default: null },
  timezone: { type: String, default: null },
})

const emit = defineEmits(['close', 'cloned'])

// Lock body scroll when modal is open
useBodyScrollLock(computed(() => props.visible))

const maxDates = 30
const defaultHour = ref('12')
const defaultMinute = ref('00')
const selectedDates = ref([])
const error = ref(null)
const loading = ref(false)
const loadingOptions = ref(false)
const currentDate = ref(new Date())
const availableCloneOptions = ref(null)

// Clone options - all default to true (UI checkboxes are checked by default)
// Service will default to false for confidentiality when no options provided (to match original behavior)
const cloneOptions = ref({
  clonePersonalLabels: true,
  cloneProjectLabels: true,
  cloneParentTask: true,
  cloneChecklist: true,
  cloneAssignee: true,
  cloneSubscribers: true,
  cloneDescription: true,
  cloneConfidential: true, // UI checkbox checked by default
  cloneConfidentialViewers: true, // UI checkbox checked by default
})

// GraphQL query to fetch available clone options
const GET_AVAILABLE_CLONE_OPTIONS = gql`
  query GetAvailableCloneOptions($projectId: ID!, $taskId: ID!) {
    project(id: $projectId) {
      task(id: $taskId) {
        id
        availableCloneOptions {
          clonePersonalLabels
          cloneProjectLabels
          cloneParentTask
          cloneChecklist
          cloneAssignee
          cloneSubscribers
          cloneDescription
          cloneConfidential
          cloneConfidentialViewers
        }
      }
    }
  }
`

const { client: apolloClient } = useApolloClient()

// Function to load clone options - use direct query to ensure fresh data every time
async function loadCloneOptions() {
  const projectId = props.projectId
  const taskId = props.task?.id
  if (!projectId || !taskId) {
    console.warn('CloneTaskModal: Missing projectId or taskId', {
      projectId,
      taskId,
      task: props.task,
    })
    loadingOptions.value = false
    return
  }

  loadingOptions.value = true
  error.value = null

  try {
    console.log('CloneTaskModal: Loading clone options', { projectId, taskId })
    const { data } = await apolloClient.query({
      query: GET_AVAILABLE_CLONE_OPTIONS,
      variables: {
        projectId: String(projectId),
        taskId: String(taskId),
      },
      fetchPolicy: 'network-only', // Always fetch from network to get fresh data
    })

    console.log('CloneTaskModal: Clone options result received', data)
    if (data?.project?.task?.availableCloneOptions) {
      availableCloneOptions.value = data.project.task.availableCloneOptions
      loadingOptions.value = false
    } else if (data?.project?.task === null) {
      // Task not found or not accessible
      console.error('CloneTaskModal: Task not found or not accessible')
      error.value = s__('TaskClone|Task not found or you do not have permission to view it')
      loadingOptions.value = false
    } else {
      console.warn('CloneTaskModal: Unexpected response structure', data)
      loadingOptions.value = false
    }
  } catch (err) {
    console.error('CloneTaskModal: Error loading clone options', err)
    error.value = err.message || s__('TaskClone|Failed to load clone options')
    loadingOptions.value = false
  }
}

// Use backend-provided clone options instead of computed properties
// This ensures we always have up-to-date information about what can be cloned
const hasClonerPersonalLabels = computed(() => {
  return availableCloneOptions.value?.clonePersonalLabels === true
})

const hasProjectLabels = computed(() => {
  return availableCloneOptions.value?.cloneProjectLabels === true
})

const hasParentTask = computed(() => {
  return availableCloneOptions.value?.cloneParentTask === true
})

const hasChecklist = computed(() => {
  return availableCloneOptions.value?.cloneChecklist === true
})

const hasAssignee = computed(() => {
  return availableCloneOptions.value?.cloneAssignee === true
})

const hasSubscribers = computed(() => {
  return availableCloneOptions.value?.cloneSubscribers === true
})

const hasDescription = computed(() => {
  return availableCloneOptions.value?.cloneDescription === true
})

const isConfidential = computed(() => {
  return availableCloneOptions.value?.cloneConfidential === true
})

const hasConfidentialViewers = computed(() => {
  return availableCloneOptions.value?.cloneConfidentialViewers === true
})

const hasAnyCloneableAttributes = computed(() => {
  if (!availableCloneOptions.value) return false
  return (
    hasClonerPersonalLabels.value ||
    hasProjectLabels.value ||
    hasParentTask.value ||
    hasChecklist.value ||
    hasAssignee.value ||
    hasSubscribers.value ||
    hasDescription.value ||
    isConfidential.value ||
    hasConfidentialViewers.value
  )
})

// Hours and minutes for 24-hour format selects
const hours = computed(() => {
  const result = []
  for (let i = 0; i < 24; i++) {
    result.push(String(i).padStart(2, '0'))
  }
  return result
})

const minutes = computed(() => ['00', '15', '30', '45'])

// Computed default time string
const defaultTime = computed(() => `${defaultHour.value}:${defaultMinute.value}`)

// Weekday labels based on locale
const weekdayLabels = computed(() => {
  const locale = props.locale || navigator.language || 'en'
  const labels = []
  const baseDate = new Date(2024, 0, 1) // Monday

  for (let i = 0; i < 7; i++) {
    const date = new Date(baseDate)
    date.setDate(baseDate.getDate() + i)
    labels.push(date.toLocaleDateString(locale, { weekday: 'short' }))
  }
  return labels
})

const monthYearDisplay = computed(() => {
  const locale = props.locale || navigator.language || 'en'
  return currentDate.value.toLocaleDateString(locale, { year: 'numeric', month: 'long' })
})

const calendarDays = computed(() => {
  const year = currentDate.value.getFullYear()
  const month = currentDate.value.getMonth()

  // First day of the month
  const firstDay = new Date(year, month, 1)
  // Last day of the month
  const lastDay = new Date(year, month + 1, 0)

  // Start from Monday of the week containing the first day
  const startDate = new Date(firstDay)
  const dayOfWeek = startDate.getDay()
  const daysFromMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1
  startDate.setDate(startDate.getDate() - daysFromMonday)

  const days = []
  const today = new Date()
  today.setHours(0, 0, 0, 0)

  // Generate 6 weeks of days (42 days)
  for (let i = 0; i < 42; i++) {
    const date = new Date(startDate)
    date.setDate(startDate.getDate() + i)

    const dateStr = formatDateKey(date)
    const isCurrentMonth = date.getMonth() === month
    const isPast = date < today

    days.push({
      day: date.getDate(),
      date: dateStr,
      otherMonth: !isCurrentMonth,
      isToday: date.getTime() === today.getTime(),
      isPast: isPast,
    })
  }

  return days
})

function formatDateKey(date) {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

function formatDateDisplay(dateStr) {
  const locale = props.locale || navigator.language || 'en'
  const date = new Date(dateStr + 'T00:00:00')
  return date.toLocaleDateString(locale, { weekday: 'short', month: 'short', day: 'numeric' })
}

function isDateSelected(dateStr) {
  return selectedDates.value.some(d => d.date === dateStr)
}

function toggleDate(day) {
  if (day.isPast && !day.isToday) return

  const existingIndex = selectedDates.value.findIndex(d => d.date === day.date)

  if (existingIndex >= 0) {
    selectedDates.value.splice(existingIndex, 1)
  } else {
    if (selectedDates.value.length >= maxDates) {
      error.value = s__('TaskClone|Maximum %{max} dates allowed') % { max: maxDates }
      return
    }
    selectedDates.value.push({
      date: day.date,
      hour: defaultHour.value,
      minute: defaultMinute.value,
    })
    // Sort by date
    selectedDates.value.sort((a, b) => a.date.localeCompare(b.date))
  }

  error.value = null
}

function removeDate(index) {
  selectedDates.value.splice(index, 1)
}

function prevMonth() {
  const newDate = new Date(currentDate.value.getFullYear(), currentDate.value.getMonth() - 1, 1)
  currentDate.value = newDate
}

function nextMonth() {
  const newDate = new Date(currentDate.value.getFullYear(), currentDate.value.getMonth() + 1, 1)
  currentDate.value = newDate
}

function close() {
  emit('close')
}

// Reset state when modal opens
watch(
  () => props.visible,
  visible => {
    if (visible) {
      selectedDates.value = []
      error.value = null
      loading.value = false
      currentDate.value = new Date()
      // Reset clone options to force fresh fetch
      availableCloneOptions.value = null
      loadingOptions.value = true
      // Clear any previous result to ensure watch fires
      // The fetchPolicy: 'network-only' will ensure we get fresh data

      // Reset clone options to defaults (UI checkboxes are checked by default)
      cloneOptions.value = {
        clonePersonalLabels: true,
        cloneProjectLabels: true,
        cloneParentTask: true,
        cloneChecklist: true,
        cloneAssignee: true,
        cloneSubscribers: true,
        cloneDescription: true,
        cloneConfidential: true, // UI checkbox checked by default
        cloneConfidentialViewers: true, // UI checkbox checked by default
      }

      // Set default time from task's due_at if available
      if (props.task?.dueAt) {
        const taskDate = new Date(props.task.dueAt)
        defaultHour.value = String(taskDate.getHours()).padStart(2, '0')
        // Round minutes to nearest 15
        const mins = Math.round(taskDate.getMinutes() / 15) * 15
        defaultMinute.value = String(mins % 60).padStart(2, '0')
      } else {
        defaultHour.value = '12'
        defaultMinute.value = '00'
      }

      // Fetch available clone options - always fetch fresh data when modal opens
      loadCloneOptions()
    }
  }
)

// GraphQL mutation
const BULK_CLONE_TASK = gql`
  mutation BulkCloneTask(
    $taskId: ID!
    $cloneDates: [CloneDateInput!]!
    $cloneOptions: CloneOptionsInput
  ) {
    bulkCloneTask(taskId: $taskId, cloneDates: $cloneDates, cloneOptions: $cloneOptions) {
      tasks {
        id
        title
        dueAt
      }
      errors
    }
  }
`

const { mutate: bulkCloneTask } = useMutation(BULK_CLONE_TASK)

async function submitClone() {
  if (selectedDates.value.length === 0) return

  loading.value = true
  error.value = null

  try {
    const cloneDates = selectedDates.value.map(d => ({
      date: d.date,
      time: `${d.hour}:${d.minute}`,
    }))

    const result = await bulkCloneTask({
      taskId: props.task.id,
      cloneDates: cloneDates,
      cloneOptions: cloneOptions.value,
    })

    if (result.data?.bulkCloneTask?.errors?.length > 0) {
      error.value = result.data.bulkCloneTask.errors.join(', ')
    } else {
      emit('cloned', result.data.bulkCloneTask.tasks)
      close()
    }
  } catch (err) {
    error.value = err.message
  } finally {
    loading.value = false
  }
}

// Template filter replacement
function format(template, values) {
  if (!template) return ''
  if (!values) return template
  let result = template
  for (const [key, value] of Object.entries(values)) {
    result = result.replace(`%{${key}}`, value)
  }
  return result
}
</script>

<style scoped>
/* Component-specific: border-radius and max-width overrides */
.modal-content {
  border-radius: 12px;
  max-width: 420px;
  font-family: var(--font-family-base);
  line-height: var(--line-height-normal);
}

.modal-content input,
.modal-content select,
.modal-content textarea,
.modal-content button {
  font-family: inherit;
}

.task-info {
  background: #f5f5f5;
  padding: 12px;
  border-radius: 8px;
  margin-bottom: 20px;
}

.task-title {
  font-weight: 500;
  color: #333;
}

.form-section {
  margin-bottom: 20px;
}

.section-label {
  display: block;
  font-weight: 500;
  margin-bottom: 8px;
  color: #333;
}

.help-text {
  font-size: 12px;
  color: #888;
  margin-bottom: 12px;
}

.time-input-24h {
  display: flex;
  align-items: center;
  gap: 4px;
}

.time-select {
  padding: 8px 12px;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 14px;
  background: #fff;
  color: #333;
  min-width: 60px;
  cursor: pointer;
}

.time-separator {
  font-size: 16px;
  font-weight: 600;
  color: #333;
}

.time-input-24h-small {
  display: flex;
  align-items: center;
  gap: 2px;
}

.time-select-small {
  padding: 4px 6px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 13px;
  background: #fff;
  color: #333;
  min-width: 50px;
  cursor: pointer;
}

.time-separator-small {
  font-size: 13px;
  font-weight: 600;
  color: #333;
}

.calendar-container {
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  overflow: hidden;
}

.calendar-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px;
  background: #f5f5f5;
}

.nav-btn {
  background: none;
  border: none;
  font-size: 20px;
  padding: 4px 12px;
  cursor: pointer;
  color: #2aabee;
  border-radius: 4px;
}

.nav-btn:hover {
  background: rgba(0, 122, 255, 0.1);
}

.month-year {
  font-weight: 600;
  color: #333;
}

.weekdays {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  text-align: center;
  padding: 8px 0;
  background: #f5f5f5;
  border-bottom: 1px solid #e0e0e0;
}

.weekday {
  font-size: 12px;
  font-weight: 500;
  color: #888;
  text-transform: uppercase;
}

.days-grid {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 2px;
  padding: 8px;
}

.day-cell {
  aspect-ratio: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  border-radius: 50%;
  transition: all 0.15s ease;
}

.day-cell:hover:not(.past):not(.other-month) {
  background: rgba(0, 122, 255, 0.1);
}

.day-number {
  font-size: 14px;
  color: #333;
}

.day-cell.other-month .day-number {
  color: #ccc;
}

.day-cell.past .day-number {
  color: #ccc;
}

.day-cell.past {
  cursor: not-allowed;
}

.day-cell.today {
  font-weight: 600;
}

.day-cell.today .day-number {
  color: #2aabee;
}

.day-cell.selected {
  background: #2aabee;
}

.day-cell.selected .day-number {
  color: #fff;
}

.selected-dates-section {
  margin-top: 20px;
  padding-top: 16px;
  border-top: 1px solid #e0e0e0;
}

.selected-dates-list {
  max-height: 200px;
  overflow-y: auto;
}

.selected-date-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 0;
  border-bottom: 1px solid #f0f0f0;
}

.selected-date-item:last-child {
  border-bottom: none;
}

.date-display {
  flex: 1;
  font-size: 14px;
  color: #333;
}

.remove-btn {
  background: none;
  border: none;
  color: #ff3b30;
  font-size: 18px;
  cursor: pointer;
  padding: 0 4px;
  line-height: 1;
}

.loading-options {
  text-align: center;
  padding: 20px;
  color: #888;
  font-size: 14px;
}

.error-message {
  background: #ffebee;
  color: #c62828;
  padding: 12px;
  border-radius: 6px;
  margin-top: 16px;
  font-size: 14px;
}

.clone-options {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.checkbox-label {
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  font-size: 14px;
  color: #333;
}

.checkbox-label input[type='checkbox'] {
  cursor: pointer;
  width: 18px;
  height: 18px;
}

@media (max-width: 767px) {
  .mobile-underlined-checklist .mobile-underlined-checklist-item.checkbox-label {
    border-radius: 0;
    background: transparent;
    margin: 0;
    width: 100%;
  }
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding: 16px 20px;
  border-top: 1px solid #e0e0e0;
}

.btn-primary,
.btn-secondary {
  padding: 10px 20px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: opacity 0.15s ease;
}

.btn-primary {
  background: #2aabee;
  color: #fff;
  border: none;
}

.btn-primary:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.btn-secondary {
  background: transparent;
  color: #2aabee;
  border: 1px solid #2aabee;
}

.btn-secondary:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
</style>
